// Floor PWA area shell.
//
// The connectivity/queue banner lives HERE rather than on each screen, and that
// is a correctness decision as much as a layout one: spec 06 §4's floor rows
// each require an offline banner and a pending indicator, and five copies of
// that logic is five chances for one screen to say "all synced" while the queue
// holds fourteen items. One banner, one queue, every screen.
//
// The queue itself is started by `useFloorOutbox()` (a per-device singleton), so
// mounting the shell is what attaches the online/focus/visibility replay
// triggers — a floor device that merely has the app open drains as soon as it
// finds signal, without anybody navigating anywhere.

import { Outlet, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SignedInAs, SyncBanner } from "./components/floor-chrome";
import { LogoutButton } from "../../routes/root";
import { LocaleSwitcher } from "../../components/locale-switcher";
import { floorOutbox, useFloorOutbox } from "./hooks/use-floor-outbox";
import { useOnline } from "./hooks/use-online";
import { guideHref, guidePageFor } from "../../lib/guide-links";
import "./screens/floor-inventory.css";

export function FloorLayout() {
  const outbox = useFloorOutbox();
  const online = useOnline();
  const { t, i18n } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="floor-area">
      <SyncBanner
        outbox={outbox}
        online={online}
        onSendNow={() => {
          void floorOutbox().sendNow();
        }}
      />
      {/* The office header is suppressed under /floor (root.tsx), so sign-out —
          which is also the device hand-over gate — lives here. So does the
          language control: floor operators never see the office header, and they
          are the population the Russian translation exists for. Who is signed
          in sits beside them, said once for every screen (D-444 ruling 1); with
          no name stamped and work queued it is a banner across the bar. Help
          opens the customer guide at this screen's page (guide §7): a
          glove-sized "?" named by its word, a plain link because /guide is the
          API's. */}
      <div className="floor-topbar">
        <SignedInAs />
        <a
          className="floor-help"
          href={guideHref(i18n.language, guidePageFor(pathname))}
          aria-label={t("nav.help")}
          title={t("nav.help")}
          data-testid="floor-help"
        >
          ?
        </a>
        <LocaleSwitcher />
        <LogoutButton />
      </div>
      <Outlet />
    </div>
  );
}
